import React, { useState } from 'react';
import { Box, Button, SpaceBetween, Input, FormField, Popover } from '@cloudscape-design/components';
import { listSessions, deleteSession, deleteAllSessions } from './sessionStore';
import AddressChip from './AddressChip';

/**
 * Session switcher — each visit is its own isolated session; the local index
 * (localStorage) lists known sessions so the user can switch between them, delete
 * them, or paste a session id to pull one (the back-end keeps the transcript,
 * 7-day TTL). Rendered as a popover to keep the single screen uncluttered.
 */
export default function SessionSwitcher({ currentId, onSwitch }) {
  const [manualId, setManualId] = useState('');
  const [tick, setTick] = useState(0); // re-read the index after mutations
  const sessions = listSessions();

  const remove = (id) => {
    deleteSession(id);
    setTick((t) => t + 1);
  };

  const removeAll = () => {
    deleteAllSessions();
    setTick((t) => t + 1);
    onSwitch(null); // start a fresh session
  };

  return (
    <Popover
      header="Sessions"
      position="bottom"
      size="large"
      triggerType="custom"
      dismissButton
      content={
        <SpaceBetween size="s">
          <Box fontSize="body-s" color="text-body-secondary">
            Each visit is isolated. Pick a past session, start a new one, or pull one by id.
            Sessions auto-delete after 7 days.
          </Box>

          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="add-plus" onClick={() => onSwitch(null)}>
              New session
            </Button>
            {sessions.length > 0 && (
              <Button iconName="remove" onClick={removeAll}>
                Delete all ({sessions.length})
              </Button>
            )}
          </SpaceBetween>

          <FormField label="Pull a session by id">
            <SpaceBetween direction="horizontal" size="xs">
              <Input
                value={manualId}
                onChange={({ detail }) => setManualId(detail.value)}
                placeholder="s_…"
              />
              <Button
                disabled={!manualId.trim()}
                onClick={() => {
                  onSwitch(manualId.trim());
                  setManualId('');
                }}
              >
                Pull
              </Button>
            </SpaceBetween>
          </FormField>

          <div key={tick}>
            {sessions.length === 0 ? (
              <Box fontSize="body-s" color="text-body-secondary">No saved sessions yet.</Box>
            ) : (
              <SpaceBetween size="xxs">
                {sessions.map((s) => (
                  <div
                    key={s.sessionId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '4px 6px',
                      borderRadius: 6,
                      background: s.sessionId === currentId ? '#f0f7ff' : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <button
                        onClick={() => onSwitch(s.sessionId)}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          padding: 0,
                          width: '100%',
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: s.sessionId === currentId ? 700 : 500 }}>
                          {s.label}
                          {s.sessionId === currentId ? ' (current)' : ''}
                        </div>
                      </button>
                      {/* session id — click to copy */}
                      <AddressChip address={s.sessionId} />
                    </div>
                    <Button variant="icon" iconName="remove" onClick={() => remove(s.sessionId)} ariaLabel="Delete session" />
                  </div>
                ))}
              </SpaceBetween>
            )}
          </div>
        </SpaceBetween>
      }
    >
      <Button iconName="folder">Sessions</Button>
    </Popover>
  );
}
